import { UploadIcon } from "lucide-react";
import { type DragEvent, type ReactNode, useRef, useState } from "react";
import { Button, type ButtonProps } from "./button";
import { cn } from "./cn";

/** A labelled region that takes dropped files and highlights while a drag is over it. */
export function DropZone({
  label,
  onFiles,
  className,
  children,
}: {
  label: string;
  onFiles: (files: File[]) => void;
  className?: string;
  children: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const drop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    onFiles([...event.dataTransfer.files]);
  };
  return (
    <section
      aria-label={label}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={cn(
        "rounded-md border border-line bg-surface",
        over && "border-primary bg-primary-soft",
        className,
      )}
    >
      {children}
    </section>
  );
}

/** A button that opens the file picker; the input stays out of the tab order. */
export function PickFilesButton({
  label,
  onFiles,
  accept,
  multiple = true,
  size = "sm",
  variant = "outline",
  icon = <UploadIcon aria-hidden />,
}: {
  label: string;
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  icon?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button size={size} variant={variant} onClick={() => input.current?.click()}>
        {icon}
        {label}
      </Button>
      <input
        ref={input}
        type="file"
        multiple={multiple}
        accept={accept}
        aria-label={label}
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          const picked = event.target.files;
          if (picked) onFiles([...picked]);
          event.target.value = "";
        }}
      />
    </>
  );
}
